import {NgTemplateOutlet} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    input,
    linkedSignal,
    output,
    signal,
    TemplateRef,
    untracked,
    viewChildren,
} from '@angular/core';
import {composeComparators, keyComparator, TranslatePipe} from '@brunata-angular/util';
import {
    applySortingToHeaders,
    CheckboxAreaDirective,
    PaginationComponent,
    SortableTableHeaderComponent,
    SortDirection,
    Sorting,
} from '@brunata-angular/ui';
import {
    SelectionTableCellContext,
    SelectionTableCellDirective,
    SelectionTableFilterDirective,
} from './selection-table-templates.directive';

export type SelectionTableRow = {
    id: string;
    selected: boolean;
};

export type SelectionTableColumn<T> = {
    key: string;
    labelKey?: string;
    compare?: (direction: SortDirection) => (a: T, b: T) => number;
    text?: (row: T) => string;
    truncate?: boolean;
    alignEnd?: boolean;
    hidden?: boolean;
    cellClass?: string;
};

type ShownColumn<T> = SelectionTableColumn<T> & {
    cell: TemplateRef<SelectionTableCellContext<T>> | null;
    filter: TemplateRef<void> | null;
    width: number | null;
};

const selectionColumn = 'selected';

@Component({
    selector: 'app-selection-table',
    standalone: true,
    imports: [
        NgTemplateOutlet,
        TranslatePipe,
        CheckboxAreaDirective,
        PaginationComponent,
        SortableTableHeaderComponent,
    ],
    template: `
        <div class="table-responsive">
            <table class="table table-striped table-hover panel-table align-middle mb-0">
                <thead>
                    <tr>
                        <th style="width: 4rem">
                            <div class="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    class="form-check-input m-0"
                                    [checked]="allSelected()"
                                    [indeterminate]="someSelected()"
                                    [disabled]="rows().length === 0"
                                    [attr.aria-label]="selectAllLabel()"
                                    (change)="toggleAll()" />
                                <div
                                    app-sortable-table-header
                                    [column]="selectionColumn"
                                    [initialDirection]="
                                        sorting().column === selectionColumn
                                            ? sorting().direction
                                            : null
                                    "
                                    (sort)="sortBy($event)"
                                    label=""></div>
                            </div>
                        </th>
                        @for (column of shownColumns(); track column.key) {
                            @if (column.compare !== undefined && column.labelKey !== undefined) {
                                <th
                                    app-sortable-table-header
                                    [class.text-end]="column.alignEnd === true"
                                    [style.width.%]="column.width"
                                    [column]="column.key"
                                    [initialDirection]="
                                        sorting().column === column.key ? sorting().direction : null
                                    "
                                    (sort)="sortBy($event)"
                                    [label]="column.labelKey | translate: 'online' : 'export'"></th>
                            } @else if (column.labelKey !== undefined) {
                                <th
                                    [class.text-end]="column.alignEnd === true"
                                    [style.width.%]="column.width">
                                    {{ column.labelKey | translate: 'online' : 'export' }}
                                </th>
                            } @else {
                                <th></th>
                            }
                        }
                    </tr>
                    @if (hasFilters()) {
                        <tr>
                            <th></th>
                            @for (column of shownColumns(); track column.key) {
                                <th [class.filter-cell]="column.filter !== null">
                                    @if (column.filter !== null) {
                                        <ng-container [ngTemplateOutlet]="column.filter" />
                                    }
                                </th>
                            }
                        </tr>
                    }
                </thead>
                <tbody>
                    @for (view of pagedRows(); track view.row.id) {
                        <tr class="selection-row" appCheckboxArea>
                            <td>
                                <div class="d-flex align-items-center">
                                    <input
                                        type="checkbox"
                                        class="form-check-input m-0"
                                        [checked]="view.row.selected"
                                        (change)="rowToggled.emit(view.row.id)"
                                        [attr.aria-label]="view.label" />
                                </div>
                            </td>
                            @for (cell of view.cells; track cell.column.key) {
                                <td
                                    [class]="cell.column.cellClass ?? ''"
                                    [class.text-end]="cell.column.alignEnd === true"
                                    [class.text-nowrap]="cell.column.truncate !== true"
                                    [class.text-truncate]="cell.column.truncate === true"
                                    [class.truncated-cell]="cell.column.truncate === true"
                                    [attr.title]="cell.column.truncate === true ? cell.text : null">
                                    @if (cell.column.cell !== null) {
                                        <ng-container
                                            [ngTemplateOutlet]="cell.column.cell"
                                            [ngTemplateOutletContext]="{$implicit: view.row}" />
                                    } @else {
                                        {{ cell.text }}
                                    }
                                </td>
                            }
                        </tr>
                    } @empty {
                        <tr>
                            <td [attr.colspan]="shownColumns().length + 1" class="text-muted small">
                                {{ emptyText() }}
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>

        <div class="p-3">
            <app-pagination
                [collectionSize]="rows().length"
                [page]="page()"
                [pageSize]="pageSize()"
                (pageChange)="page.set($event)"
                (pageSizeChange)="pageSize.set($event)"></app-pagination>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectionTableComponent<T extends SelectionTableRow> {
    private readonly headers = viewChildren(SortableTableHeaderComponent);
    private readonly cellTemplates = contentChildren(SelectionTableCellDirective);
    private readonly filterTemplates = contentChildren(SelectionTableFilterDirective);

    readonly rows = input.required<ReadonlyArray<T>>();
    readonly columns = input.required<ReadonlyArray<SelectionTableColumn<T>>>();
    readonly sortedBy = input.required<string>();
    readonly rowLabel = input.required<(row: T) => string>();
    readonly selectAllLabel = input.required<string>();
    readonly emptyText = input.required<string>();

    readonly rowToggled = output<string>();
    readonly rowsSelected = output<string[]>();
    readonly rowsDeselected = output<string[]>();

    protected readonly selectionColumn = selectionColumn;

    protected readonly sorting = linkedSignal<Sorting>(() => ({
        column: this.sortedBy(),
        direction: 'asc',
    }));

    protected readonly pageSize = signal(10);

    private readonly rowIds = computed(() =>
        this.rows()
            .map(row => row.id)
            .join('\n'),
    );

    protected readonly page = linkedSignal({
        source: () => [this.rowIds(), this.sorting(), this.pageSize()],
        computation: () => 1,
    });

    protected readonly shownColumns = computed((): ShownColumn<T>[] => {
        const cells = new Map(
            this.cellTemplates().map(directive => [
                directive.appSelectionTableCell(),
                directive.template,
            ]),
        );
        const filters = new Map(
            this.filterTemplates().map(directive => [
                directive.appSelectionTableFilter(),
                directive.template,
            ]),
        );
        const shown = this.columns().filter(column => column.hidden !== true);
        const truncatedCount = shown.filter(column => column.truncate === true).length;
        return shown.map(column => ({
            ...column,
            cell: cells.get(column.key) ?? null,
            filter: filters.get(column.key) ?? null,
            width: column.truncate === true ? 100 / truncatedCount : null,
        }));
    });

    protected readonly hasFilters = computed(() =>
        this.shownColumns().some(column => column.filter !== null),
    );

    private readonly selectedWhenSorted = linkedSignal({
        source: this.sorting,
        computation: () =>
            new Set(
                untracked(this.rows)
                    .filter(row => row.selected)
                    .map(row => row.id),
            ),
    });

    private readonly comparator = computed((): ((a: T, b: T) => number) | undefined => {
        const {column, direction} = this.sorting();
        const compareBy = (
            key: string,
            order: SortDirection,
        ): ((a: T, b: T) => number) | undefined =>
            this.columns()
                .find(candidate => candidate.key === key)
                ?.compare?.(order);
        if (column !== selectionColumn) {
            return compareBy(column, direction);
        }
        const selected = this.selectedWhenSorted();
        const byDefault = compareBy(this.sortedBy(), 'asc');
        return composeComparators<T>([
            keyComparator<T, number>(row => (selected.has(row.id) ? 0 : 1), direction),
            ...(byDefault === undefined ? [] : [byDefault]),
        ]);
    });

    private readonly sortedRows = computed(() => {
        const comparator = this.comparator();
        return comparator === undefined ? [...this.rows()] : [...this.rows()].sort(comparator);
    });

    protected readonly pagedRows = computed(() => {
        const start = (this.page() - 1) * this.pageSize();
        const columns = this.shownColumns();
        const rowLabel = this.rowLabel();
        return this.sortedRows()
            .slice(start, start + this.pageSize())
            .map(row => ({
                row,
                label: rowLabel(row),
                cells: columns.map(column => ({column, text: column.text?.(row) ?? ''})),
            }));
    });

    protected readonly allSelected = computed(
        () => this.rows().length > 0 && this.rows().every(row => row.selected),
    );

    protected readonly someSelected = computed(
        () => !this.allSelected() && this.rows().some(row => row.selected),
    );

    protected sortBy(sorting: Sorting): void {
        this.sorting.set(sorting);
        applySortingToHeaders(this.headers(), sorting);
    }

    protected toggleAll(): void {
        const ids = this.rows().map(row => row.id);
        if (this.allSelected()) {
            this.rowsDeselected.emit(ids);
        } else {
            this.rowsSelected.emit(ids);
        }
    }
}
